// TODO: ENG-1600: generate JWTs on server - these are temporary hardcoded JWTs per environment
import OneSchemaImporter from '@oneschema/react';
import { UploadIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/core/button';
import { Dialog } from '@/components/core/dialog';
import { DEPLOYMENT_STAGE, ONESCHEMA_CLIENT_ID } from '@/env';
import { extractErrorMessage, formatShortInt } from '@/shared/helpers';
import { useBatchedRequests } from '@/shared/hooks/useBatchedRequests';
import { useMessages } from '@/shared/providers/MessagesProvider';

import { RunnerDialogContent } from './runner-dialog-content';
import { TemplateKey } from './types';
import { useImportRequests } from './use-import-requests';

const DEV_USER_JWT =
  'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJjNzBhZTA5Mi1mOTdkLTQ0OWYtOGYzZC1lNzA2ZTU5YjNmYzciLCJ1c2VyX2lkIjoiPFVTRVJfSUQ-In0.Zyn_FA-MwYlPxXmPm-eZQ5Ww7Gv0j3YkWbfYFFdvT_s';
const PROD_USER_JWT =
  'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJ1c2VyX2lkIjoiPFVTRVJfSUQ-IiwiaXNzIjoiMTZkNjQ5ZGEtMzlmZi00ZmFhLTk3MTMtNWEzYmJiYTZmY2E3In0.DMgWrrPqNb6e2c5ryLjOkuZUCVV_rpO4EBC-vBXINgs';

type Props = {
  template: TemplateKey;
  onComplete?: () => void;
};

export function DataImporter({ template, onComplete }: Props) {
  const messages = useMessages();
  const [isImporterOpen, setIsImporterOpen] = useState(false);
  const [isRunnerDialogOpen, setIsRunnerDialogOpen] = useState(false);

  const importRequests = useImportRequests(template);
  const { run, results, failures, isRunning, total, completed } = useBatchedRequests<
    Record<string, any>,
    any
  >({ latestFirst: true });
  const created = useMemo(() => results.map((result) => result.output), [results]);

  const processImportData = async (data: { records: Record<string, any>[] }) => {
    setIsImporterOpen(false);
    setIsRunnerDialogOpen(true);
    try {
      await run(importRequests(data.records));
      messages.success('Import completed');
      onComplete?.();
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      messages.error(`Import failed${errorMessage ? `: ${errorMessage}` : ''}`);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setIsImporterOpen(true)} loading={isImporterOpen}>
        <UploadIcon />
        {isRunning
          ? `Importing ${formatShortInt(completed)}/${formatShortInt(total ?? 0)}`
          : 'Import'}
      </Button>

      <OneSchemaImporter
        isOpen={isImporterOpen}
        onRequestClose={() => setIsImporterOpen(false)}
        clientId={ONESCHEMA_CLIENT_ID}
        devMode={DEPLOYMENT_STAGE !== 'PROD'}
        // TODO: ENG-1600: generate JWT with account_id / user_id
        userJwt={DEPLOYMENT_STAGE === 'PROD' ? PROD_USER_JWT : DEV_USER_JWT}
        templateKey={template}
        importConfig={{ type: 'local' }}
        onSuccess={(data) => void processImportData(data)}
        onCancel={() => setIsImporterOpen(false)}
        onError={(err) => {
          messages.error(err.message);
        }}
        className="oneschema-importer"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1000,
        }}
      />

      <Dialog open={isRunnerDialogOpen} onOpenChange={setIsRunnerDialogOpen}>
        <RunnerDialogContent
          template={template}
          running={isRunning}
          totalCount={total}
          completedCount={completed}
          createdRecords={created}
          errorRecords={failures}
        />
      </Dialog>
    </>
  );
}
